/**
 * The small presentational pieces of a provider card: its status dot, its
 * one-line summary, and when it was last checked.
 *
 * @module
 */
import { cn } from "../../lib/utils";
import { formatRelativeTime } from "../../timestampFormat";
import { PROVIDER_SETTINGS_ICON_BY_PROVIDER } from "../providerSettingsIcons";
import { Skeleton } from "../ui/skeleton";
import { PROVIDER_STATUS_STYLES } from "./providerAuthCopy";
import { ProviderSummary } from "./providerSummary";
import { useRelativeTimeTick } from "./settingsLayout";
import { ProviderKind } from "@cmmd-forge/contracts";

export function ProviderStatusIcon({
  provider,
  statusStyle,
  isLoading,
}: {
  provider: ProviderKind;
  statusStyle: (typeof PROVIDER_STATUS_STYLES)[keyof typeof PROVIDER_STATUS_STYLES];
  isLoading: boolean;
}) {
  const ProviderIcon = PROVIDER_SETTINGS_ICON_BY_PROVIDER[provider];

  if (!ProviderIcon) {
    return isLoading ? (
      <Skeleton className="size-2 shrink-0 rounded-full" />
    ) : (
      <span className={cn("size-2 shrink-0 rounded-full", statusStyle.dot)} />
    );
  }

  return (
    <span className="relative inline-flex size-5 shrink-0 items-center justify-center">
      <ProviderIcon aria-hidden="true" className="size-4" />
      {isLoading ? (
        <Skeleton
          aria-hidden="true"
          className="pointer-events-none absolute -left-0.5 -top-0.5 size-2 rounded-full ring-2 ring-background"
        />
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute -left-0.5 -top-0.5 size-2 rounded-full ring-2 ring-background",
            statusStyle.dot,
          )}
        />
      )}
    </span>
  );
}

export function ProviderSummaryText({ summary }: { summary: ProviderSummary }) {
  // Loading state: shimmer the subtitle row so it's visually clear we're still
  // fetching, not stuck in a warning.
  if (summary.state === "loading") {
    return <Skeleton className="h-3 w-32 rounded-full" />;
  }
  // Authenticated and idle: green status dot says it all.
  if (!summary.headline && !summary.detail) return null;
  return <p className="text-xs text-muted-foreground">{summary.headline}</p>;
}

export function ProviderLastChecked({ lastCheckedAt }: { lastCheckedAt: string | null }) {
  useRelativeTimeTick();
  const lastCheckedRelative = lastCheckedAt ? formatRelativeTime(lastCheckedAt) : null;

  if (!lastCheckedRelative) {
    return null;
  }

  return (
    <span className="text-[11px] text-muted-foreground/60">
      {lastCheckedRelative.suffix ? (
        <>
          Checked <span className="font-mono tabular-nums">{lastCheckedRelative.value}</span>{" "}
          {lastCheckedRelative.suffix}
        </>
      ) : (
        <>Checked {lastCheckedRelative.value}</>
      )}
    </span>
  );
}
